/**
 * Pure comparison logic for the materials tab and the supplier comparison
 * page: one column per supplier of a material, best/worst highlighting per
 * metric, flags and the monthly series the charts plot.
 */
import { getContract } from '../../data/contracts';
import {
  getMaterial,
  materialHistory,
  materialSuppliers,
} from '../../data/materials';
import { getSupplier, isSupplierId } from '../../data/suppliers';
import type {
  Material,
  MaterialId,
  MonthlyPoint,
  Supplier,
  SupplierId,
  SupplierMaterialMetrics,
} from '../../data/types';

export interface SupplierColumn {
  metrics: SupplierMaterialMetrics;
  supplier: Supplier;
  /** 24 months, oldest first. Empty when the supplier has no volume yet. */
  history: MonthlyPoint[];
  /** Trailing 12 months spend (canonical) plus the prior 12 months from history. */
  spend24m: number;
}

/** Spend on the prior 12 months (history indexes 0-11): units received times price paid. */
function priorYearSpend(history: MonthlyPoint[]): number {
  return history
    .slice(0, 12)
    .reduce((sum, point) => sum + point.volume * point.pricePaid, 0);
}

export function buildSupplierColumns(materialId: MaterialId): SupplierColumn[] {
  return materialSuppliers(materialId).map((metrics) => {
    const history = materialHistory(materialId, metrics.supplierId);
    return {
      metrics,
      supplier: getSupplier(metrics.supplierId),
      history,
      spend24m: metrics.spendTtm + priorYearSpend(history),
    };
  });
}

export function activeColumns(columns: SupplierColumn[]): SupplierColumn[] {
  return columns.filter((column) => column.metrics.status === 'active');
}

// ---------------------------------------------------------------------------
// Metric highlighting
// ---------------------------------------------------------------------------

export type MetricDirection = 'min' | 'max';
export type MetricSeverity = 'amber' | 'red';

export interface MetricCell {
  /** Best value among the compared suppliers (never set on a tie for all). */
  best: boolean;
  /** Set only when the value is materially bad. */
  severity: MetricSeverity | null;
}

export type SeverityRule = (
  value: number,
  best: number,
) => MetricSeverity | null;

export interface MetricRule {
  direction: MetricDirection;
  severity: SeverityRule;
}

function relativeGapRule(amberAt: number): SeverityRule {
  return (value, best) =>
    best > 0 && value / best - 1 >= amberAt ? 'amber' : null;
}

export const METRIC_RULES = {
  unitPrice: { direction: 'min', severity: relativeGapRule(0.05) },
  landedPrice: { direction: 'min', severity: relativeGapRule(0.05) },
  // TCO is all-in, so a 1% gap is already worth flagging on a $60M spend base.
  tco: { direction: 'min', severity: relativeGapRule(0.01) },
  leadTime: {
    direction: 'min',
    severity: (value, best) =>
      value >= 10 && value - best >= 4 ? 'amber' : null,
  },
  otif: {
    direction: 'max',
    severity: (value) => {
      if (value < 92) return 'red';
      return value < 95 ? 'amber' : null;
    },
  },
  ppm: {
    direction: 'min',
    severity: (value) => {
      // 500 PPM is the incoming-quality limit in SA-CORV-2024-017 section 8.1.
      if (value > 500) return 'red';
      return value > 300 ? 'amber' : null;
    },
  },
  ncrs: {
    direction: 'min',
    severity: (value) => {
      if (value >= 3) return 'red';
      return value >= 2 ? 'amber' : null;
    },
  },
} satisfies Record<string, MetricRule>;

export type RuledMetric = keyof typeof METRIC_RULES;

/**
 * Evaluate one metric across suppliers. `null` values (not measured) are
 * skipped. The best value is only marked when at least two suppliers have a
 * value and they are not all equal.
 */
export function evaluateMetric(
  values: (number | null)[],
  rule: MetricRule,
): MetricCell[] {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) {
    return values.map(() => ({ best: false, severity: null }));
  }
  const best =
    rule.direction === 'min' ? Math.min(...present) : Math.max(...present);
  const allEqual = present.every((v) => v === best);
  const canMarkBest = present.length >= 2 && !allEqual;
  return values.map((value) => {
    if (value === null) return { best: false, severity: null };
    // Absolute rules (OTIF, PPM) can flag even the best value; never badge a
    // value as best when it is also materially bad.
    const severity = rule.severity(value, best);
    return {
      best: canMarkBest && value === best && severity === null,
      severity,
    };
  });
}

// ---------------------------------------------------------------------------
// Material summary rows
// ---------------------------------------------------------------------------

export type FlagTone = 'warning' | 'danger' | 'neutral';

export interface MaterialFlag {
  label: string;
  tone: FlagTone;
}

/** Open sourcing events tied to a material (shared IDs from the README). */
export const MATERIAL_SOURCING_EVENT: Partial<Record<MaterialId, string>> = {
  'MAT-LED': 'RFP-2026-014',
  'MAT-VFD': 'RFQ-2026-022',
};

export function materialFlags(columns: SupplierColumn[]): MaterialFlag[] {
  const flags: MaterialFlag[] = [];
  const active = activeColumns(columns);
  if (active.length === 1) {
    flags.push({ label: 'Single source', tone: 'warning' });
  }
  const escape = active.find((column) => (column.metrics.ppm ?? 0) >= 1000);
  if (escape) {
    flags.push({ label: 'Quality problem', tone: 'danger' });
  }
  const renewal = active.find((column) => {
    const { contractId } = column.metrics;
    return (
      contractId !== null && getContract(contractId).status === 'in_renewal'
    );
  });
  if (renewal) {
    flags.push({ label: 'Renewal due', tone: 'warning' });
  }
  const qualifying = columns.find(
    (column) => column.metrics.status === 'qualifying',
  );
  if (qualifying) {
    flags.push({
      label: `${qualifying.supplier.shortName} being approved`,
      tone: 'neutral',
    });
  }
  return flags;
}

export interface LowestCost {
  supplierId: SupplierId;
  value: number;
  /** TCO when the material models it, otherwise the landed price. */
  basis: 'tco' | 'landed';
}

export function lowestCostSupplier(
  columns: SupplierColumn[],
): LowestCost | null {
  const active = activeColumns(columns);
  if (active.length === 0) return null;
  const hasTco = active.every((column) => column.metrics.tco !== null);
  let best: LowestCost | null = null;
  for (const column of active) {
    const value = hasTco
      ? (column.metrics.tco ?? column.metrics.landedPrice)
      : column.metrics.landedPrice;
    if (best === null || value < best.value) {
      best = {
        supplierId: column.metrics.supplierId,
        value,
        basis: hasTco ? 'tco' : 'landed',
      };
    }
  }
  return best;
}

export interface MaterialSummary {
  material: Material;
  columns: SupplierColumn[];
  activeCount: number;
  /** Unit price range over active suppliers. */
  priceMin: number;
  priceMax: number;
  spendTtm: number;
  lowestCost: LowestCost | null;
  flags: MaterialFlag[];
}

export function summarizeMaterial(materialId: MaterialId): MaterialSummary {
  const columns = buildSupplierColumns(materialId);
  const active = activeColumns(columns);
  const prices = active.map((column) => column.metrics.unitPrice);
  return {
    material: getMaterial(materialId),
    columns,
    activeCount: active.length,
    priceMin: prices.length > 0 ? Math.min(...prices) : 0,
    priceMax: prices.length > 0 ? Math.max(...prices) : 0,
    spendTtm: active.reduce((sum, column) => sum + column.metrics.spendTtm, 0),
    lowestCost: lowestCostSupplier(columns),
    flags: materialFlags(columns),
  };
}

/** Share-weighted unit price across active suppliers. */
export function blendedUnitPrice(columns: SupplierColumn[]): number {
  const active = activeColumns(columns);
  const totalShare = active.reduce(
    (sum, column) => sum + column.metrics.share,
    0,
  );
  if (totalShare === 0) return 0;
  return (
    active.reduce(
      (sum, column) => sum + column.metrics.unitPrice * column.metrics.share,
      0,
    ) / totalShare
  );
}

// ---------------------------------------------------------------------------
// Chart series
// ---------------------------------------------------------------------------

export type ChartRow = { month: string } & Partial<Record<SupplierId, number>>;

export type SeriesMetric = 'pricePaid' | 'otif' | 'ppm' | 'volume';

/**
 * One row per month with a value per supplier, limited to the last `months`
 * months. Suppliers without history are left out of the rows.
 */
export function buildChartRows(
  columns: SupplierColumn[],
  metric: SeriesMetric,
  months: number,
): ChartRow[] {
  const withHistory = columns.filter((column) => column.history.length > 0);
  const first = withHistory[0];
  if (!first) return [];
  const start = Math.max(0, first.history.length - months);
  return first.history.slice(start).map((point, offset) => {
    const row: ChartRow = { month: point.month };
    for (const column of withHistory) {
      const value = column.history[start + offset]?.[metric];
      if (value !== undefined) row[column.metrics.supplierId] = value;
    }
    return row;
  });
}

export interface NiceScale {
  domain: [number, number];
  ticks: number[];
}

/**
 * Round axis bounds outward to a 1, 2, 2.5 or 5 step so the Y axis shows
 * clean tick values (for example $410, $420 ... $450).
 */
export function niceScale(min: number, max: number, tickCount = 4): NiceScale {
  if (!(max > min)) {
    const base = Math.abs(min) || 1;
    return niceScale(min - base * 0.05, max + base * 0.05, tickCount);
  }
  const rawStep = (max - min) / tickCount;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const factor = [1, 2, 2.5, 5, 10].find((f) => f * magnitude >= rawStep) ?? 10;
  const step = factor * magnitude;
  const lo = Math.floor(min / step + 1e-9) * step;
  const hi = Math.ceil(max / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let value = lo; value <= hi + step / 1000; value += step) {
    ticks.push(Number(value.toPrecision(12)));
  }
  return {
    domain: [Number(lo.toPrecision(12)), Number(hi.toPrecision(12))],
    ticks,
  };
}

/** Axis scale for the values of a series (plus any reference values). */
export function seriesScale(
  rows: ChartRow[],
  columns: SupplierColumn[],
  extra: number[] = [],
  floorAtZero = false,
): NiceScale {
  const values: number[] = [...extra];
  for (const row of rows) {
    for (const column of columns) {
      const value = row[column.metrics.supplierId];
      if (value !== undefined) values.push(value);
    }
  }
  if (values.length === 0) return { domain: [0, 1], ticks: [0, 1] };
  const min = floorAtZero ? 0 : Math.min(...values);
  return niceScale(min, Math.max(...values));
}

/** Months in the history where the price paid exceeded the contract price. */
export function monthsAboveContract(history: MonthlyPoint[]): MonthlyPoint[] {
  return history.filter(
    (point) => point.pricePaid - point.contractPrice > 0.005,
  );
}

// ---------------------------------------------------------------------------
// Share scenario
// ---------------------------------------------------------------------------

export interface ShareSlice {
  supplierId: SupplierId;
  name: string;
  share: number;
}

export function sharesToSlices(
  shares: Partial<Record<SupplierId, number>>,
): ShareSlice[] {
  const slices: ShareSlice[] = [];
  for (const [id, share] of Object.entries(shares)) {
    if (share === undefined || !isSupplierId(id)) continue;
    slices.push({ supplierId: id, name: getSupplier(id).shortName, share });
  }
  return slices;
}
